import React, { useState, useEffect, useCallback } from 'react';

const API = 'https://api.botanix.in/api';

const C = {
 bg:'#0A1628', surface:'#0F2040', surface2:'#162A40',
 border:'rgba(30,58,95,.8)', border2:'rgba(30,58,95,1)',
 text:'#F0F2FF', text2:'#A0A8C0', text3:'#606880',
 accent:'#22C55E', blue:'#FFFFFF',
 red:'#EF4444', redBg:'rgba(239,68,68,.10)', redBorder:'rgba(239,68,68,.25)',
 amber:'#F59E0B', amberBg:'rgba(245,158,11,.10)', amberBorder:'rgba(245,158,11,.25)',
 green:'#22C55E', greenBg:'rgba(34,197,94,.10)', greenBorder:'rgba(34,197,94,.25)',
};

const inr = n => '₹' + Number(n||0).toLocaleString('en-IN',{maximumFractionDigits:0});
const inp = { width:'100%', padding:'10px 12px', background:'rgba(255,255,255,.05)', border:`1px solid ${C.border2}`, borderRadius:8, color:C.text, fontSize:13, boxSizing:'border-box', outline:'none', fontFamily:'Inter,sans-serif' };
const sel = { ...inp, cursor:'pointer' };

const CATEGORIES = ['All','Grains','Dairy','Vegetables','Meat','Pulses','Oils','Beverages','Dry Goods','Poultry','Other'];
const UNITS = ['kg','L','pcs','g','dozen','box','pack'];

const Label = ({children}) => <label style={{ display:'block', fontSize:11, color:C.text2, marginBottom:4, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em' }}>{children}</label>;

export default function Inventory() {
 const [items, setItems] = useState([]);
 const [loading, setLoading] = useState(true);
 const [search, setSearch] = useState('');
 const [filterCat, setFilterCat] = useState('All');
 const [filterLow, setFilterLow] = useState(false);
 const [showAdd, setShowAdd] = useState(false);
 const [editItem, setEditItem] = useState(null);
 const [saving, setSaving] = useState(false);
 const [error, setError] = useState('');
 const [success, setSuccess] = useState('');
 const [showStock, setShowStock] = useState(null);
 const [stockQty, setStockQty] = useState('');
 const [stockType, setStockType] = useState('in');
 const [tab, setTab] = useState('items');

 const [form, setForm] = useState({
 item_name:'', category:'Grains', unit:'kg',
 current_stock:0, reorder_level:0, average_cost:0,
 expiry_tracking_required:false,
 });

 const load = useCallback(async () => {
 setLoading(true);
 try {
 const r = await fetch(`${API}/inventory`);
 const d = await r.json();
 setItems(Array.isArray(d)?d:[]);
 } catch(e) { setError(e.message); }
 finally { setLoading(false); }
 }, []);

 useEffect(() => { load(); }, [load]);

 const showMsg = (msg, isErr=false) => {
 if(isErr) setError(msg); else setSuccess(msg);
 setTimeout(()=>{ setError(''); setSuccess(''); }, 3000);
 };

 const handleSave = async () => {
 if (!form.item_name.trim()) { showMsg('Enter item name', true); return; }
 setSaving(true);
 try {
 const url = editItem ? `${API}/inventory/${editItem.id}` : `${API}/inventory`;
 const method = editItem ? 'PUT' : 'POST';
 const r = await fetch(url,{method,headers:{'Content-Type':'application/json'},body:JSON.stringify(form)});
 if (!r.ok) throw new Error('Save failed');
 showMsg(editItem?'Item updated!':'Item added!');
 setShowAdd(false); setEditItem(null);
 setForm({item_name:'',category:'Grains',unit:'kg',current_stock:0,reorder_level:0,average_cost:0,expiry_tracking_required:false});
 load();
 } catch(e) { showMsg(e.message, true); }
 finally { setSaving(false); }
 };

 const handleDelete = async (id) => {
 if (!window.confirm('Delete this item?')) return;
 try {
 await fetch(`${API}/inventory/${id}`,{method:'DELETE'});
 showMsg('Item deleted!');
 load();
 } catch(e) { showMsg(e.message, true); }
 };

 const handleStockUpdate = async () => {
 if (!stockQty) return;
 setSaving(true);
 try {
 const qty = parseFloat(stockQty);
 const newStock = stockType==='in'
 ? parseFloat(showStock.current_stock||0) + qty
 : parseFloat(showStock.current_stock||0) - qty;
 await fetch(`${API}/inventory/${showStock.id}`,{
 method:'PUT', headers:{'Content-Type':'application/json'},
 body:JSON.stringify({...showStock, current_stock: Math.max(0,newStock)}),
 });
 showMsg(`Stock ${stockType==='in'?'added':'removed'} successfully!`);
 setShowStock(null); setStockQty('');
 load();
 } catch(e) { showMsg(e.message, true); }
 finally { setSaving(false); }
 };

 const openEdit = (item) => {
 setEditItem(item);
 setForm({
 item_name:item.item_name, category:item.category||'Grains',
 unit:item.unit||'kg', current_stock:item.current_stock||0,
 reorder_level:item.reorder_level||0, average_cost:item.average_cost||0,
 expiry_tracking_required:item.expiry_tracking_required||false,
 });
 setShowAdd(true);
 };

 // Filter items
 let filtered = items;
 if (search) filtered = filtered.filter(i=>i.item_name?.toLowerCase().includes(search.toLowerCase())||i.category?.toLowerCase().includes(search.toLowerCase()));
 if (filterCat!=='All') filtered = filtered.filter(i=>i.category===filterCat);
 if (filterLow) filtered = filtered.filter(i=>i.is_low_stock);

 const lowStockCount = items.filter(i=>i.is_low_stock).length;
 const totalValue = items.reduce((s,i)=>s+parseFloat(i.current_stock||0)*parseFloat(i.average_cost||0),0);
 const categories = [...new Set(items.map(i=>i.category).filter(Boolean))];

 return (
 <div style={{ padding:24, background:C.bg, minHeight:'100vh', color:C.text, fontFamily:'Inter,sans-serif' }}>

 {/* Header */}
 <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
 <div>
 <div style={{ fontSize:22, fontWeight:800 }}> Inventory Management</div>
 <div style={{ fontSize:13, color:C.text2, marginTop:2 }}>Live stock tracking · Kitchen & Resort supplies</div>
 </div>
 <div style={{ display:'flex', gap:10 }}>
 <button onClick={load} style={{ padding:'9px 16px', background:C.surface, border:`1px solid ${C.border2}`, borderRadius:8, color:C.text2, cursor:'pointer', fontSize:13 }}> Refresh</button>
 <button onClick={()=>{setEditItem(null);setForm({item_name:'',category:'Grains',unit:'kg',current_stock:0,reorder_level:0,average_cost:0,expiry_tracking_required:false});setShowAdd(true);}}
 style={{ padding:'9px 18px', background:C.accent, border:'none', borderRadius:8, color:'#fff', cursor:'pointer', fontSize:13, fontWeight:700 }}>+ Add Item</button>
 </div>
 </div>

 {error && <div style={{ padding:'10px 14px', background:C.redBg, border:`1px solid ${C.redBorder}`, borderRadius:8, color:C.red, fontSize:13, marginBottom:16 }}> {error}</div>}
 {success && <div style={{ padding:'10px 14px', background:C.greenBg,border:`1px solid ${C.greenBorder}`,borderRadius:8, color:C.green, fontSize:13, marginBottom:16 }}> {success}</div>}

 {/* Stats */}
 <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:12, marginBottom:20 }}>
 {[
 { label:'Total Items', value:items.length, color:C.accent, },
 { label:'Low Stock', value:lowStockCount, color:lowStockCount>0?C.red:C.accent, },
 { label:'Total Value', value:inr(totalValue), color:C.amber, icon:'₹' },
 { label:'Categories', value:categories.length, color:'#FFFFFF', },
 ].map(s=>(
 <div key={s.label} style={{ background:C.surface, border:`1px solid ${s.color}33`, borderRadius:12, padding:16 }}>
 <div style={{ fontSize:20, marginBottom:6 }}>{s.icon}</div>
 <div style={{ fontSize:22, fontWeight:900, color:s.color }}>{s.value}</div>
 <div style={{ fontSize:11, color:C.text3, marginTop:4 }}>{s.label}</div>
 </div>
 ))}
 </div>

 {/* Low stock alert */}
 {lowStockCount > 0 && (
 <div style={{ padding:'12px 16px', background:C.redBg, border:`1px solid ${C.redBorder}`, borderRadius:10, marginBottom:16, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
 <div style={{ fontSize:13, color:C.red, fontWeight:600 }}> {lowStockCount} item{lowStockCount>1?'s':''} below reorder level!</div>
 <button onClick={()=>setFilterLow(true)} style={{ padding:'5px 12px', background:C.red, border:'none', borderRadius:6, color:'#fff', cursor:'pointer', fontSize:12, fontWeight:600 }}>View Low Stock</button>
 </div>
 )}

 {/* Filters */}
 <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
 <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search items..."
 style={{ ...inp, flex:1, maxWidth:280 }} />
 <select value={filterCat} onChange={e=>setFilterCat(e.target.value)} style={{ ...sel, width:160 }}>
 {CATEGORIES.map(c=><option key={c} style={{background:'#0F2040',color:'#F0F2FF'}}>{c}</option>)}
 </select>
 <button onClick={()=>setFilterLow(p=>!p)}
 style={{ padding:'10px 14px', background:filterLow?C.red:'rgba(239,68,68,.1)', border:`1px solid ${C.redBorder}`, borderRadius:8, color:filterLow?'#fff':C.red, cursor:'pointer', fontSize:13, fontWeight:600 }}>
 Low Stock {filterLow?'(ON)':''}
 </button>
 </div>

 {loading && <div style={{ textAlign:'center', padding:40, color:C.text2 }}>Loading inventory…</div>}

 {/* Items table */}
 {!loading && (
 <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:14, overflow:'hidden' }}>
 <table style={{ width:'100%', borderCollapse:'collapse' }}>
 <thead>
 <tr style={{ borderBottom:`1px solid ${C.border}` }}>
 {['Item Name','Category','Unit','Current Stock','Reorder Level','Avg Cost','Stock Value','Status','Actions'].map(h=>(
 <th key={h} style={{ padding:'10px 14px', fontSize:11, color:C.text2, fontWeight:700, textAlign:'left', textTransform:'uppercase' }}>{h}</th>
 ))}
 </tr>
 </thead>
 <tbody>
 {filtered.map((item,i)=>{
 const stockVal = parseFloat(item.current_stock||0)*parseFloat(item.average_cost||0);
 const isLow = item.is_low_stock;
 return (
 <tr key={item.id} style={{ borderBottom:`1px solid rgba(30,58,95,.5)`, background:i%2===0?'transparent':C.surface2 }}>
 <td style={{ padding:'10px 14px', fontSize:13, fontWeight:600, color:C.text }}>{item.item_name}</td>
 <td style={{ padding:'10px 14px', fontSize:12, color:C.text2 }}>
 <span style={{ padding:'2px 8px', background:'rgba(34,197,94,.1)', borderRadius:4, fontSize:11 }}>{item.category}</span>
 </td>
 <td style={{ padding:'10px 14px', fontSize:12, color:C.text2 }}>{item.unit}</td>
 <td style={{ padding:'10px 14px', fontSize:13, fontWeight:700, color:isLow?C.red:C.accent }}>
 {parseFloat(item.current_stock||0).toFixed(1)} {item.unit}
 </td>
 <td style={{ padding:'10px 14px', fontSize:12, color:C.text2 }}>{parseFloat(item.reorder_level||0).toFixed(1)} {item.unit}</td>
 <td style={{ padding:'10px 14px', fontSize:12, color:C.text2 }}>{inr(item.average_cost)}/{item.unit}</td>
 <td style={{ padding:'10px 14px', fontSize:12, fontWeight:600, color:C.amber }}>{inr(stockVal)}</td>
 <td style={{ padding:'10px 14px' }}>
 <span style={{ padding:'3px 8px', borderRadius:99, fontSize:10, fontWeight:700,
 background:isLow?C.redBg:C.greenBg, color:isLow?C.red:C.green }}>
 {isLow?' Low':' OK'}
 </span>
 </td>
 <td style={{ padding:'10px 14px' }}>
 <div style={{ display:'flex', gap:5 }}>
 <button onClick={()=>{ setShowStock(item); setStockQty(''); setStockType('in'); }}
 style={{ padding:'5px 10px', background:C.greenBg, border:`1px solid ${C.greenBorder}`, borderRadius:6, color:C.green, cursor:'pointer', fontSize:11, fontWeight:600 }}>
 + Stock
 </button>
 <button onClick={()=>openEdit(item)}
 style={{ padding:'5px 10px', background:'rgba(255,255,255,.06)', border:`1px solid ${C.border}`, borderRadius:6, color:C.text2, cursor:'pointer', fontSize:11 }}>
 
 </button>
 <button onClick={()=>handleDelete(item.id)}
 style={{ padding:'5px 8px', background:C.redBg, border:`1px solid ${C.redBorder}`, borderRadius:6, color:C.red, cursor:'pointer', fontSize:11 }}>
 
 </button>
 </div>
 </td>
 </tr>
 );
 })}
 {filtered.length===0 && (
 <tr><td colSpan={9} style={{ textAlign:'center', padding:40, color:C.text2 }}>No items found</td></tr>
 )}
 </tbody>
 </table>
 </div>
 )}

 {/* Add/Edit Modal */}
 {showAdd && (
 <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.75)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
 <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:16, padding:24, maxWidth:480, width:'100%' }}>
 <div style={{ fontWeight:700, fontSize:16, color:C.text, marginBottom:20 }}>{editItem?'Edit':'Add'} Inventory Item</div>
 <div style={{ display:'grid', gap:14 }}>
 <div>
 <Label>Item Name *</Label>
 <input style={inp} value={form.item_name} onChange={e=>setForm(p=>({...p,item_name:e.target.value}))} placeholder="e.g. Basmati Rice" />
 </div>
 <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
 <div>
 <Label>Category</Label>
 <select style={sel} value={form.category} onChange={e=>setForm(p=>({...p,category:e.target.value}))}>
 {CATEGORIES.filter(c=>c!=='All').map(c=><option key={c} style={{background:'#0F2040',color:'#F0F2FF'}}>{c}</option>)}
 </select>
 </div>
 <div>
 <Label>Unit</Label>
 <select style={sel} value={form.unit} onChange={e=>setForm(p=>({...p,unit:e.target.value}))}>
 {UNITS.map(u=><option key={u} style={{background:'#0F2040',color:'#F0F2FF'}}>{u}</option>)}
 </select>
 </div>
 </div>
 <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
 <div>
 <Label>Current Stock</Label>
 <input style={inp} type="number" value={form.current_stock} onChange={e=>setForm(p=>({...p,current_stock:e.target.value}))} min={0} />
 </div>
 <div>
 <Label>Reorder Level</Label>
 <input style={inp} type="number" value={form.reorder_level} onChange={e=>setForm(p=>({...p,reorder_level:e.target.value}))} min={0} />
 </div>
 <div>
 <Label>Avg Cost (₹/{form.unit})</Label>
 <input style={inp} type="number" value={form.average_cost} onChange={e=>setForm(p=>({...p,average_cost:e.target.value}))} min={0} />
 </div>
 </div>
 <div style={{ display:'flex', alignItems:'center', gap:10 }}>
 <input type="checkbox" checked={form.expiry_tracking_required} onChange={e=>setForm(p=>({...p,expiry_tracking_required:e.target.checked}))} style={{ width:16, height:16, cursor:'pointer', accentColor:C.accent }} />
 <label style={{ fontSize:13, color:C.text, cursor:'pointer' }}>Track expiry date</label>
 </div>
 </div>
 <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:20 }}>
 <button onClick={()=>{setShowAdd(false);setEditItem(null);}} style={{ padding:'10px 18px', background:'none', border:`1px solid ${C.border2}`, borderRadius:8, color:C.text2, cursor:'pointer' }}>Cancel</button>
 <button onClick={handleSave} disabled={saving} style={{ padding:'10px 24px', background:C.accent, border:'none', borderRadius:8, color:'#fff', cursor:'pointer', fontWeight:700, opacity:saving?.6:1 }}>
 {saving?'Saving…':editItem?'Update':'Add Item'}
 </button>
 </div>
 </div>
 </div>
 )}

 {/* Stock Update Modal */}
 {showStock && (
 <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.75)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
 <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:16, padding:24, maxWidth:380, width:'100%' }}>
 <div style={{ fontWeight:700, fontSize:16, color:C.text, marginBottom:6 }}>Update Stock</div>
 <div style={{ fontSize:13, color:C.text2, marginBottom:16 }}>{showStock.item_name} · Current: {parseFloat(showStock.current_stock||0).toFixed(1)} {showStock.unit}</div>
 <div style={{ display:'flex', gap:8, marginBottom:14 }}>
 <button onClick={()=>setStockType('in')} style={{ flex:1, padding:'10px', background:stockType==='in'?C.accent:'rgba(255,255,255,.06)', border:`1px solid ${stockType==='in'?C.accent:C.border}`, borderRadius:8, color:stockType==='in'?'#fff':C.text2, cursor:'pointer', fontWeight:600, fontSize:13 }}>+ Stock In</button>
 <button onClick={()=>setStockType('out')} style={{ flex:1, padding:'10px', background:stockType==='out'?C.red:'rgba(255,255,255,.06)', border:`1px solid ${stockType==='out'?C.red:C.border}`, borderRadius:8, color:stockType==='out'?'#fff':C.text2, cursor:'pointer', fontWeight:600, fontSize:13 }}>- Stock Out</button>
 </div>
 <Label>Quantity ({showStock.unit})</Label>
 <input style={{ ...inp, marginBottom:16, fontSize:18, fontWeight:700 }} type="number" value={stockQty} onChange={e=>setStockQty(e.target.value)} placeholder="Enter quantity" min={0} />
 {stockQty && (
 <div style={{ padding:'8px 12px', background:stockType==='in'?C.greenBg:C.redBg, borderRadius:8, fontSize:13, color:stockType==='in'?C.green:C.red, marginBottom:14 }}>
 New stock: {(parseFloat(showStock.current_stock||0)+(stockType==='in'?1:-1)*parseFloat(stockQty||0)).toFixed(1)} {showStock.unit}
 </div>
 )}
 <div style={{ display:'flex', gap:10 }}>
 <button onClick={()=>setShowStock(null)} style={{ flex:1, padding:'10px', background:'none', border:`1px solid ${C.border2}`, borderRadius:8, color:C.text2, cursor:'pointer' }}>Cancel</button>
 <button onClick={handleStockUpdate} disabled={saving||!stockQty}
 style={{ flex:1, padding:'10px', background:stockType==='in'?C.accent:C.red, border:'none', borderRadius:8, color:'#fff', cursor:'pointer', fontWeight:700, opacity:(saving||!stockQty)?.5:1 }}>
 {saving?'Saving…':'Confirm'}
 </button>
 </div>
 </div>
 </div>
 )}
 </div>
 );
}
